import React, { useState, useEffect, Suspense } from 'react';
import { Shield, ShieldCheck, Lock, Eye, EyeOff, FileText, ClipboardList, Globe, MessageSquare, MessageSquareText, Activity, Code2, Send, Layers, CloudOff, Zap, Sparkles, BookMarked, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BrandEmblem } from './BrandEmblem';
import { useAuth } from './AuthProvider';

const ComplianceOnePagerPage = React.lazy(() => import('../pages/ComplianceOnePagerPage'));
const SecurityFaqPage = React.lazy(() => import('../pages/SecurityFaqPage'));
const ProcurementPacketPage = React.lazy(() => import('../pages/ProcurementPacketPage'));
const ReviewerHubPage = React.lazy(() => import('../pages/ReviewerHubPage'));

const SITE_PASSWORD = process.env.REACT_APP_SITE_PASSWORD || "";
const STORAGE_KEY = "writeright_site_access";
const NDA_KEY = "writeright_nda_accepted";

const SiteGate = ({ children }) => {
  const { isAuthenticated: isUserAuthenticated, user } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [showNdaModal, setShowNdaModal] = useState(false);
  const [ndaFadeIn, setNdaFadeIn] = useState(false);
  const [ndaTextVisible, setNdaTextVisible] = useState(false);
  // Mar 4, 2026 — allow the "Compliance One-Pager" link in the footer
  // to open a fullscreen legal summary page ABOVE the gate (works pre-auth).
  const [showCompliance, setShowCompliance] = useState(false);
  // Jul 11, 2026 — Security & Privacy FAQ (same pre-auth overlay pattern).
  const [showSecurityFaq, setShowSecurityFaq] = useState(false);
  // Jul 11, 2026 — Procurement Packet (combined kit for Contracting Officers).
  const [showProcurement, setShowProcurement] = useState(false);
  // Jul 12, 2026 (v6.9.9) — Read-only Compliance Reviewer mode. Detected
  // via `?reviewer=1` in the URL, this replaces the provider workspace
  // entirely with a hub of the 4 compliance PDFs. Bypasses gate & NDA
  // (session-only; no localStorage write). Safe for JAG / CO handoff.
  const [reviewerMode] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('reviewer') === '1';
    } catch { return false; }
  });

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      sessionStorage.setItem(STORAGE_KEY, 'granted');
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }
    // Jul 12, 2026 (v6.9.9) — Reviewer-mode bypass. If `?reviewer=1` is
    // present, skip everything and let render() show the ReviewerHubPage.
    // No gate, no NDA, no login. Read-only view.
    try {
      const qp = new URLSearchParams(window.location.search);
      if (qp.get('reviewer') === '1') {
        setNdaAccepted(true);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }
    } catch (_) { /* ignore */ }

    // Jul 12, 2026 (v6.9.8) — Screenshot-mode bypass for automated patent
    // capture. When the URL contains `?screenshot_mode=1` we skip both the
    // NDA modal and the site password gate. Safe because:
    //   • The Emergent preview URL is auto-generated and not indexed;
    //   • This bypass is session-scoped only (no localStorage write);
    //   • It does NOT log the user in — Playwright still has to POST
    //     to /api/auth/login with real credentials to reach provider mode.
    try {
      const qp = new URLSearchParams(window.location.search);
      if (qp.get('screenshot_mode') === '1') {
        setNdaAccepted(true);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }
    } catch (_) { /* ignore */ }

    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === 'granted') {
      setIsAuthenticated(true);
    }
    // Check if NDA was previously accepted (persists across sessions for logged-in users)
    const ndaStored = localStorage.getItem(NDA_KEY);
    if (ndaStored === 'accepted') {
      setNdaAccepted(true);
    }
    setIsLoading(false);
  }, []);

  // If the user successfully signs in through the AuthModal (or Google OAuth),
  // auto-grant site access — they've passed a stronger auth than the site password.
  // Without this, the gate stays up even though the app thinks they're signed in.
  useEffect(() => {
    if (isUserAuthenticated && user && !user.is_guest) {
      sessionStorage.setItem(STORAGE_KEY, 'granted');
      // Logged-in users have already accepted Terms via signup — auto-accept NDA for them too
      localStorage.setItem(NDA_KEY, 'accepted');
      setNdaAccepted(true);
      setIsAuthenticated(true);
    }
  }, [isUserAuthenticated, user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!ndaAccepted) {
      setShowNdaModal(true);
      setTimeout(() => setNdaFadeIn(true), 50);
      setTimeout(() => setNdaTextVisible(true), 2500);
      return;
    }
    if (password === SITE_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, 'granted');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };

  const handleNdaAccept = () => {
    localStorage.setItem(NDA_KEY, 'accepted');
    setNdaAccepted(true);
    setShowNdaModal(false);
    setNdaFadeIn(false);
    setNdaTextVisible(false);

    // Fire-and-forget server-side audit log (legal defensibility).
    // Failure does NOT block the user — local acceptance is still honored.
    try {
      const apiUrl = window.__WR_API_BASE__;
      if (apiUrl) {
        fetch(`${apiUrl}/api/nda/acknowledge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            client_version: 'v1-2026-04-22',
            client_text_sha256: null, // server canonicalizes
          }),
        }).catch(() => {});
      }
    } catch (_) { /* noop */ }
  };

  const handlePasswordFocus = () => {
    if (!ndaAccepted) {
      setShowNdaModal(true);
      setTimeout(() => setNdaFadeIn(true), 50);
      setTimeout(() => setNdaTextVisible(true), 2500);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#082850] flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Reviewer mode replaces the provider workspace with a read-only hub.
    if (reviewerMode) {
      return (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-cyan-600" /></div>}>
          <ReviewerHubPage />
        </Suspense>
      );
    }
    return children;
  }

  // Compliance One-Pager overlay renders ABOVE the gate so JAG / procurement
  // can review it without providing the site password.
  if (showCompliance) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#082850' }}><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>}>
        <ComplianceOnePagerPage onBack={() => setShowCompliance(false)} />
      </Suspense>
    );
  }

  // Security & Privacy FAQ overlay — same pre-auth pattern.
  if (showSecurityFaq) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#082850' }}><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>}>
        <SecurityFaqPage onBack={() => setShowSecurityFaq(false)} />
      </Suspense>
    );
  }

  // Procurement Packet overlay — combined compliance kit for CO/JAG review.
  if (showProcurement) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#082850' }}><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>}>
        <ProcurementPacketPage onBack={() => setShowProcurement(false)} />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto" data-testid="site-gate" style={{ backgroundColor: '#082850' }}>

      {/* ═══ SIGN-IN CARD — Centered (vertically centered above-fold on desktop) ═══ */}
      <div className="flex justify-center px-4 lg:min-h-[calc(100vh-2rem)] lg:items-center pt-6 sm:pt-8 lg:pt-0">
        <div className="w-full max-w-md py-6">
          {/* Hero tagline above the card */}
          <div className="text-center mb-5">
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#94a3b8' }}>
              From ONE voice or text input...
              <br />
              produces FOUR or more essential documents...
              <br />
              <span className="font-black uppercase tracking-wide" style={{
                background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
              }}>SIMULTANEOUSLY!</span>
            </p>
            <p className="text-xs mt-2" style={{ color: '#64748b' }}>
              HIPAA-compliant clinical charting tool for military providers, medics, and ancillary clinical staff.
            </p>
          </div>

          <div className="rounded-2xl p-6 sm:p-8 relative" style={{
            backgroundColor: '#0a3468',
            border: '1px solid rgba(218,165,32,0.15)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)'
          }}>
            <div className="flex items-center justify-center mb-4 relative z-10">
              <div className="flex items-center relative" style={{ pointerEvents: 'none' }}>
                <img
                  src="/emblem_icon.png"
                  alt="WriteRight PCM+ Logo - Clinical Documentation Platform"
                  className="absolute opacity-80"
                  style={{ width: 140, height: 140, left: -14, objectFit: 'contain', zIndex: 0 }}
                />
                <div style={{ position: 'relative', zIndex: 1, marginLeft: 72, marginTop: 20, textAlign: 'left' }}>
                  <div style={{ fontSize: 28, fontWeight: 'bold', color: '#e2e8f0', lineHeight: 1.1 }}>WriteRight</div>
                  <div style={{
                    fontSize: 28, fontWeight: 'bold', lineHeight: 1.1, marginBottom: 3, paddingLeft: 6,
                    background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
                  }}>PCM+</div>
                  <div style={{ fontSize: 12, fontWeight: 'bold', color: '#e2e8f0', paddingLeft: 6 }}>People First</div>
                </div>
              </div>
            </div>

            <p className="text-xs text-center mb-5" style={{ color: '#94a3b8' }}>
              <span className="font-semibold" style={{
                background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
              }}>Converging quality and quantity.</span>{' '}
              <span className="font-bold" style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
              }}>Simultaneously.</span>{' '}
              <span style={{ color: '#94a3b8' }}>Without compromise.</span>
            </p>

            <div className="space-y-3 relative z-10">
              <div className="text-center">
                <Lock className="w-4 h-4 inline mr-1.5" style={{ color: '#DAA520' }} />
                <span className="text-xs" style={{ color: '#94a3b8' }}>Authorized personnel — preview access</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={handlePasswordFocus}
                    placeholder={ndaAccepted ? "Enter access password" : "Tap to acknowledge terms first"}
                    className="h-11 pr-14 relative z-20"
                    style={{ backgroundColor: 'rgba(8,40,80,0.8)', borderColor: ndaAccepted ? 'rgba(218,165,32,0.3)' : 'rgba(218,165,32,0.1)', color: '#e2e8f0', borderRadius: '2px' }}
                    autoComplete="off"
                    inputMode="text"
                    readOnly={!ndaAccepted}
                    data-testid="gate-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-9 h-9 flex items-center justify-center rounded-md transition-colors hover:bg-white/10"
                    style={{ color: '#DAA520' }}
                    data-testid="gate-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={2.25} /> : <Eye className="w-5 h-5" strokeWidth={2.25} />}
                  </button>
                </div>

                {error && <p className="text-red-400 text-sm text-center">{error}</p>}

                <Button
                  type="submit"
                  className="w-full h-11 font-semibold"
                  style={{
                    background: 'linear-gradient(180deg, #DAA520 0%, #8B6914 40%, #6B4F10 100%)',
                    color: '#fff',
                    boxShadow: '0 4px 20px rgba(218,165,32,0.25), 0 2px 6px rgba(0,0,0,0.3)',
                    letterSpacing: '0.03em',
                    borderRadius: '2px'
                  }}
                  data-testid="gate-submit-button"
                >
                  Access Site
                </Button>
              </form>

              <p className="text-[11px] text-center" style={{ color: '#64748b' }}>
                Contact admin for access credentials.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ 3-COLUMN: Patient | Hero Image | Provider ═══ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-10 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-8 items-stretch">

          {/* Mar 3, 2026 — Order swapped so Providers lead on both mobile and
              desktop. Mobile single-column stack: Hero → Provider → Patient.
              Desktop 3-column: Provider (left) / Hero (middle) / Patient (right).
              Providers = patent moat + revenue audience, so they see the moat
              first regardless of device. */}

          {/* RIGHT — FOR PATIENTS (LUXE GOLD TILE — warm gold parchment, navy ink) */}
          <div className="order-3 relative p-6 sm:p-7 overflow-hidden" style={{
            background: 'linear-gradient(155deg, #f4d47a 0%, #d4a441 35%, #b8860b 70%, #8b6508 100%)',
            borderTop: '1px solid rgba(255,255,255,0.5)',
            borderLeft: '1px solid rgba(255,255,255,0.35)',
            borderRight: '1px solid rgba(80,50,0,0.5)',
            borderBottom: '1px solid rgba(50,30,0,0.7)',
            borderRadius: '2px',
            boxShadow: `
              0 2px 0 rgba(255,236,180,0.4) inset,
              0 -2px 0 rgba(80,50,0,0.35) inset,
              0 20px 50px rgba(0,0,0,0.6),
              0 8px 24px rgba(139,101,11,0.25)
            `,
          }} data-testid="gate-patient-benefits">
            {/* Gold foil highlight sheen */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(165deg, rgba(255,255,255,0.22) 0%, transparent 25%, transparent 70%, rgba(255,255,255,0.08) 100%)',
            }} />
            {/* Deep blue watermark emblem */}
            <img src="/emblem_icon.png" alt="" className="absolute pointer-events-none select-none"
              style={{ width: 200, height: 200, bottom: -30, right: -20, opacity: 0.09, filter: 'brightness(0.3) sepia(1) hue-rotate(200deg) saturate(3)' }} />
            <h2 className="text-base sm:text-lg font-black tracking-widest uppercase mb-4 text-center relative z-10"
              style={{ color: '#0c2752', textShadow: '0 1px 0 rgba(255,236,180,0.6)' }}>
              For Patients &amp; Beneficiaries
            </h2>
            <p className="text-sm leading-relaxed mb-5 relative z-10 font-medium" style={{ color: '#0a1f3d' }}>
              Walk into your exam room with <strong style={{ color: '#000915' }}>the words that get heard</strong>. WriteRight PCM+ turns your concerns into dialogue — and your dialogue into documents — for clarity at every appointment, no matter how short or pressed for time.
            </p>

            <div className="space-y-3.5 relative z-10">
              {[
                { Icon: MessageSquareText, title: 'Visit Prep Script', desc: 'Evidence-based talking points grounded in DoD/VA protocols. Know what to say when the system tries to default.' },
                { Icon: FileText, title: 'Admin Letters', desc: 'FMLA, ADA accommodations, duty limitations, PCS stabilization, LOD memos — pre-drafted for your provider.' },
                { Icon: Activity, title: 'NSI Concussion Tracker', desc: 'The same symptom inventory your PCM uses. Score it, log it, bring it to your appointment.' },
                { Icon: MessageSquare, title: 'Portal Messages', desc: 'Draft clear, professional messages to your PCM that actually get answered.' },
                { Icon: Globe, title: 'Spanish Language Support', desc: 'Full patient letters and education materials generated in Spanish for bilingual families.' },
                { Icon: Shield, title: 'No PII Stored', desc: 'Guest mode saves nothing on our servers. Safe to use on any device.' },
              ].map(({ Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 shrink-0" style={{ background: 'rgba(12,39,82,0.18)', border: '1px solid rgba(12,39,82,0.35)', borderRadius: '2px' }}>
                    <Icon className="w-4 h-4" style={{ color: '#0c2752' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#000915' }}>{title}</p>
                    {/* Feb 27, 2026 — subtext was #2d3e5e (mid-navy) which
                        had insufficient contrast against the gold gradient.
                        Bumped to a deeper near-black navy + font-medium so
                        descriptions are crisp on every monitor/glare level. */}
                    <p className="text-xs leading-relaxed font-medium" style={{ color: '#0a1f3d' }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CENTER — Hero Image (wide but height-capped so chain doesn't stretch tall) */}
          <div className="order-1 lg:order-2 flex justify-center items-center relative z-10 py-4 lg:py-0 lg:px-4">
            <img
              src="/gate_hero.png"
              alt="Gold stethoscope and hourglass - Healthcare meets time efficiency - WriteRight PCM+ Clinical Documentation"
              className="relative object-contain"
              style={{
                width: '100%',
                maxWidth: 420,
                maxHeight: 260,
                filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.55))',
              }}
            />
          </div>

          {/* LEFT — FOR PROVIDERS (IDENTICAL LUXE GOLD TILE — monochromatic mirror of right) */}
          <div className="order-2 lg:order-1 relative p-6 sm:p-7 overflow-hidden" style={{
            background: 'linear-gradient(155deg, #f4d47a 0%, #d4a441 35%, #b8860b 70%, #8b6508 100%)',
            borderTop: '1px solid rgba(255,255,255,0.5)',
            borderLeft: '1px solid rgba(255,255,255,0.35)',
            borderRight: '1px solid rgba(80,50,0,0.5)',
            borderBottom: '1px solid rgba(50,30,0,0.7)',
            borderRadius: '2px',
            boxShadow: `
              0 2px 0 rgba(255,236,180,0.4) inset,
              0 -2px 0 rgba(80,50,0,0.35) inset,
              0 20px 50px rgba(0,0,0,0.6),
              0 8px 24px rgba(139,101,11,0.25)
            `,
          }} data-testid="gate-provider-benefits">
            {/* Gold foil highlight sheen */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(165deg, rgba(255,255,255,0.22) 0%, transparent 25%, transparent 70%, rgba(255,255,255,0.08) 100%)',
            }} />
            {/* Deep blue watermark emblem — mirrored to left side for visual balance */}
            <img src="/emblem_icon.png" alt="" className="absolute pointer-events-none select-none"
              style={{ width: 200, height: 200, bottom: -30, left: -20, opacity: 0.09, filter: 'brightness(0.3) sepia(1) hue-rotate(200deg) saturate(3)' }} />
            <h2 className="text-base sm:text-lg font-black tracking-widest uppercase mb-4 text-center relative z-10"
              style={{ color: '#0c2752', textShadow: '0 1px 0 rgba(255,236,180,0.6)' }}>
              For Providers
            </h2>
            <p className="text-sm leading-relaxed mb-5 relative z-10 font-medium" style={{ color: '#0a1f3d' }}>
              Dictate once. Walk out with <strong style={{ color: '#000915' }}>the entire encounter packet</strong> — clinical note, ICD-10 codes, orders, referrals, and admin letters — all generated <strong style={{ color: '#000915' }}>simultaneously</strong> in the language of DoD/VA charting. Fast <em>and</em> defensible, without the trade-off.
            </p>

            <div className="space-y-3.5 relative z-10">
              {[
                // Mar 3, 2026 — Refreshed to lead with the clinical-AI moat
                // (patent lever) rather than the generic "we generate notes"
                // list. Each item here reflects an actual differentiator
                // versus commercial ambient-scribes (Nuance DAX, Nabla,
                // Suki, Abridge): concurrent multi-doc generation, regulatory-
                // grade formatting, 3-layer safety net, ICD-10+E/M coupling,
                // clinical tone selector, verbatim dot-phrase honoring.
                { Icon: Zap, title: 'One Voice → 4 Documents · Simultaneously', desc: 'Note + ICD-10/E-M coding + specialty referrals + admin letter — all generated from a single dictation in one round-trip. No copy-paste, no re-prompting.' },
                { Icon: ShieldCheck, title: 'DoD / VA / AR 25-50 Formatting Locked In', desc: 'Military abbreviations, TBI/PRA phrasing, ADSM vs civilian voice, unit-line signature block, MM/DD/YY HHMM hrs timestamps. Not generic AI prose — regulatory-grade output.' },
                { Icon: Shield, title: '3-Layer Clinical Safety Net', desc: 'Pre-generation demographic guard + strict LLM demographic scrubber + verified-demographics pill on-screen. A 68 y/o male can\'t become a 2 y/o female on your note. Ever.' },
                { Icon: Code2, title: 'ICD-10 Smart Help + E/M Complexity Match', desc: 'Live code suggestions surface as you type. Codes are matched to your diagnosis language AND visit acuity — E/M level reasoning included, not guessed.' },
                { Icon: Sparkles, title: 'Clinical Tone Selector', desc: 'Concise · Thorough · Teaching · Procedural. Same encounter, four voices — pick the register that fits the reader (attending, resident, JAG, insurance auditor).' },
                { Icon: BookMarked, title: 'Dot-Phrase Honoring (Verbatim)', desc: 'Your saved macros and clinical phrases render exactly as written — AI writes around your language, never over it. Chain multiple referrals in one visit without touching the note.' },
                { Icon: Layers, title: 'Speed Chart Batch + Offline Resilience', desc: 'Multi-panel dictation grid for high-tempo clinic days. Lost signal? Drafts queue locally with one-tap Copy-All / Download-.txt for emergency EMR paste-in.' },
              ].map(({ Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 shrink-0" style={{ background: 'rgba(12,39,82,0.18)', border: '1px solid rgba(12,39,82,0.35)', borderRadius: '2px' }}>
                    <Icon className="w-4 h-4" style={{ color: '#0c2752' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#000915' }}>{title}</p>
                    <p className="text-xs leading-relaxed font-medium" style={{ color: '#0a1f3d' }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ─── Footer — Health Classification (visible to crawlers) ─── */}
      <footer className="py-5 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto text-center space-y-2.5">
          <p className="text-xs" style={{ color: '#94a3b8' }}>
            WriteRight PCM+ — Clinical Documentation & Healthcare Navigation Platform
          </p>
          <p className="text-xs" style={{ color: '#64748b' }}>
            Designed for NP, PA, MD, DO, and Medic providers in military primary care settings.
            Supports clinical encounter notes, ICD-10 coding, referral generation, imaging orders,
            and administrative letters (FMLA, ADA, LOD, PCS stabilization).
          </p>

          {/* ─── Mar 4, 2026 — Audit-Ready Trust Badge ───
              Sales knockout for military legal offices and hospital
              procurement. Each pill echoes a specific clause in the
              BAA + Privacy Policy so a JAG reviewer can verify. */}
          <div
            className="flex flex-wrap items-center justify-center gap-1.5 pt-2"
            data-testid="audit-ready-trust-badge"
          >
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
              style={{ background: 'rgba(6,182,212,0.08)', borderColor: 'rgba(6,182,212,0.35)', color: '#67e8f9' }}
              data-testid="badge-transient-processing"
              title="Content is held only for the seconds it takes to generate — see Privacy Policy §3 and BAA §2.1(a), §2.1(d)"
            >
              <ShieldCheck className="w-3 h-3" /> Transient Processing
            </span>
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
              style={{ background: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.35)', color: '#6ee7b7' }}
              data-testid="badge-no-training"
              title="Your content is never used to train, fine-tune, or improve any AI model — see Privacy Policy §2 and BAA §2.4"
            >
              <ShieldCheck className="w-3 h-3" /> No AI Training on Your Content
            </span>
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
              style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)', color: '#fcd34d' }}
              data-testid="badge-audit-log"
              title="Metadata-only, hash-chained, 6-year retention, verifiable on demand — see BAA §3.2(d)"
            >
              <ShieldCheck className="w-3 h-3" /> Tamper-Evident Audit Log
            </span>
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
              style={{ background: 'rgba(139,92,246,0.08)', borderColor: 'rgba(139,92,246,0.35)', color: '#c4b5fd' }}
              data-testid="badge-baa"
              title="BAA available with No-Designated-Record-Set clause — see BAA §3.9. Contact us for signature."
            >
              <ShieldCheck className="w-3 h-3" /> BAA Available
            </span>
          </div>

          {/* Compliance One-Pager link — clickable trigger for the PDF-ready
              legal summary page. Anchored in the footer where JAG / MTF
              procurement typically look for downloadable summaries. */}
          <div className="pt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <button
              onClick={() => setShowCompliance(true)}
              className="text-[11px] underline underline-offset-2 hover:text-cyan-300 transition-colors"
              style={{ color: '#94a3b8' }}
              data-testid="compliance-onepager-link"
            >
              Download the Compliance One-Pager (PDF)
            </button>
            <button
              onClick={() => setShowSecurityFaq(true)}
              className="text-[11px] underline underline-offset-2 hover:text-cyan-300 transition-colors"
              style={{ color: '#94a3b8' }}
              data-testid="security-faq-link"
            >
              Security &amp; Privacy FAQ (PDF)
            </button>
            <button
              onClick={() => setShowProcurement(true)}
              className="text-[11px] underline underline-offset-2 hover:text-cyan-300 transition-colors"
              style={{ color: '#94a3b8' }}
              data-testid="procurement-packet-link"
            >
              Compliance Kit — Procurement Packet (PDF)
            </button>
          </div>

          <p className="text-xs" style={{ color: '#64748b' }}>
            HIPAA-compliant &bull; No patient data stored &bull; Compatible with MHS Genesis EHR
          </p>
        </div>
      </footer>

      {/* ═══ NDA ACKNOWLEDGMENT MODAL — Cinematic (Blue Velvet + Emblem Hold) ═══ */}
      {showNdaModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{
          background: 'radial-gradient(ellipse at center, #1a3a6e 0%, #0a1f3d 45%, #030a18 100%)',
        }}>
          {/* Phase 1: Emblem on blue velvet — holds 2.5s before NDA text reveals */}
          <div
            className="absolute inset-0 flex items-center justify-center transition-opacity duration-1000"
            style={{
              opacity: ndaFadeIn && !ndaTextVisible ? 1 : 0,
              pointerEvents: ndaTextVisible ? 'none' : 'auto',
            }}
          >
            <div className="flex flex-col items-center gap-5">
              <img
                src="/emblem_icon.png"
                alt="WriteRight PCM+ — People First"
                style={{
                  width: 200,
                  height: 200,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 0 40px rgba(218,165,32,0.55)) drop-shadow(0 0 80px rgba(218,165,32,0.25))',
                  transform: ndaFadeIn ? 'scale(1)' : 'scale(0.92)',
                  transition: 'transform 1000ms ease-out',
                }}
              />
              <p
                className="text-xs tracking-[0.4em] uppercase"
                style={{
                  color: 'rgba(218,165,32,0.7)',
                  fontWeight: 600,
                }}
              >
                WriteRight PCM+
              </p>
              <p
                className="text-[10px] tracking-[0.3em] uppercase"
                style={{ color: 'rgba(226,232,240,0.4)' }}
              >
                People First
              </p>
            </div>
          </div>

          {/* Phase 2: NDA Text */}
          <div className={`relative max-w-lg mx-4 transition-all duration-700 ${ndaTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="p-6 border" style={{ backgroundColor: 'rgba(8,40,80,0.95)', borderColor: 'rgba(218,165,32,0.3)', borderRadius: '2px' }}>
              <div className="text-center mb-4">
                <p className="text-sm tracking-widest uppercase mb-4" style={{ color: '#94a3b8' }}>Welcome to</p>
                <div className="flex justify-center">
                  <BrandEmblem size="md" />
                </div>
              </div>
              
              <div className="space-y-3 text-sm leading-relaxed" style={{ color: '#94a3b8' }}>
                <p>
                  You are about to access a clinical documentation tool designed to facilitate your care 
                  in a way not available before. In good faith of having pre-access to this tool:
                </p>
                <div className="p-3" style={{ backgroundColor: 'rgba(218,165,32,0.08)', borderLeft: '3px solid rgba(218,165,32,0.5)', borderRadius: '2px' }}>
                  <p className="text-xs" style={{ color: '#cbd5e1' }}>
                    I acknowledge that <strong>WriteRight PCM+</strong> is proprietary software currently under 
                    independent patent review. I agree not to replicate, redistribute, reverse-engineer, 
                    or create derivative works from any functionality, interface design, or clinical workflow 
                    contained within this application. All rights, intellectual property, and proceeds belong 
                    solely to the creator. This acknowledgment is stored for the protection of all parties.
                  </p>
                </div>
                <p className="text-xs" style={{ color: '#64748b' }}>
                  Your acknowledgment is recorded securely (timestamp, version, session fingerprint). By proceeding, you agree to these terms.
                </p>
              </div>
              
              <div className="mt-5 flex flex-col gap-2">
                <Button
                  onClick={handleNdaAccept}
                  className="w-full h-11 text-sm font-bold"
                  style={{ background: 'linear-gradient(180deg, #DAA520 0%, #B8860B 100%)', color: '#082850', borderRadius: '2px' }}
                  data-testid="nda-accept-btn"
                >
                  I Acknowledge &amp; Agree
                </Button>
                <button
                  onClick={() => { setShowNdaModal(false); setNdaFadeIn(false); setNdaTextVisible(false); }}
                  className="text-xs py-2 transition-colors"
                  style={{ color: '#64748b' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteGate;
